import React, { useCallback, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { useDropzone } from "react-dropzone"
import update from "immutability-helper"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { Absolute, Box, Row } from "@motorata/base-ui.components.atoms._atoms";
import Button, { IconButton } from "@motorata/base-ui.components.atoms.button";
import { P } from "@motorata/base-ui.components.atoms.typography";
import Spinner from "@motorata/base-ui.components.atoms.spinner";
import { makeid } from "@/utils/stringHelper";
import EmptyBlock from "@/components/shared/EmptyBlock";
import { FileService } from "@/services/FileService";

const BlockWrapper = ({ id, children, index, moveCard }) => {
  const ref = useRef(null)
  const [{ handlerId }, drop] = useDrop({
    accept: "card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      // @ts-ignore
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveCard(dragIndex, hoverIndex)
      // @ts-ignore
      item.index = hoverIndex
    },
  })
  const [{ isDragging }, drag] = useDrag({
    type: "card",
    item: () => {
      return { id, index }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = isDragging ? 0 : 1
  drag(drop(ref))
  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId}>
      {children}
    </div>
  )
}

const Gallery = ({ images, setImages }) => {
  const [isUploading, setIsUploading] = useState(false)
  const [showGallery, setShowGallery] = useState(false)

  const { acceptedFiles, getRootProps, getInputProps, isDragAccept } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': []
    },
    maxFiles: 30,
    onDrop: acceptedFiles => {
      const data = acceptedFiles.map(file => {
        return {
          id: makeid(10),
          isUploading: true,
          src: URL.createObjectURL(file),
          file,
        }
      })

      setImages([...images, ...data])
    },
  })

  const uploadFile = async item => {
    try {
      const res = await FileService.uploadFile(item.file)
      const data = images
      const index = data.findIndex(el => el.id === item.id)
      data[index] = res
      setImages(data)
    } catch (e) {
      console.log("API Error: Can not upload the file")
    }
  }

  const uploadFiles = async data => {
    setIsUploading(true)
    await Promise.all(data.map(item => uploadFile(item)))
    setIsUploading(false)
  }

  useEffect(() => {
    if (!isUploading) {
      const data = images.filter(el => el.isUploading)
      if (data.length > 0) {
        uploadFiles(data)
      }
    }
  }, [isUploading, images])

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const dragCard = images[dragIndex]
      const newArray = update(images, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard],
          ],
        })


      setImages(newArray)
    },
    [images],
  )

  const handleAddButton = useCallback(() => {
    // @ts-ignore
    document.querySelector("#upload-input").click()
  }, [])

  const handleDelete = useCallback(
    item => {
      const data = images.filter(el => el.id !== item.id)
      setImages(data)
    },
    [images],
  )

  const renderImages = (image, index) => {
    return (
      <BlockWrapper key={image.id} index={index} id={image.id} moveCard={moveCard}>
        <ImageBlockWrapper active={index === 0}>
          {!image.isUploading && <img src={image.src} />}
          {image.isUploading && <Spinner />}
          <Absolute className="trash_wrapper" right={10} top={10} display="none">
            <IconButton icon="trash" onClick={() => handleDelete(image)} />
          </Absolute>
        </ImageBlockWrapper>
      </BlockWrapper>
    )
  }

  return (
    <>
      <DropzoneWrapper
        {...getRootProps({
          onClick: event => event.stopPropagation(),
        })}
      >
        <Box>
          <Row marginBottom="S" justifyContent="flex-end">
            <Button icon="plus" onClick={handleAddButton}>
              Dodaj zdjęcie
            </Button>
            <input id="upload-input" {...getInputProps()} />
          </Row>
          <ImagesWrapper>
            {images.length > 0 && (
              <DndProvider backend={HTML5Backend}>
                <ImagesContent>{images.map((card, i) => renderImages(card, i))}</ImagesContent>
              </DndProvider>
            )}
            {images.length === 0 && (
              <EmptyBlock text="Upuść tutaj lub kliknij przycisk aby dodać zdjęcie. Możesz maksymalnie dodać 30 zdjęć." />
            )}
            {isDragAccept && (
              <DropzoneBackground>
                <P>Upuść pliki, aby je dodać</P>
              </DropzoneBackground>
            )}
          </ImagesWrapper>
        </Box>
      </DropzoneWrapper>
    </>
  )
}

export default Gallery

const ImageBlockWrapper = styled(Box)<{
  active: boolean
}>`
  width: 100px;
  height: 100px;
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${({ active }) => active && css`
    border: 2px solid ${({ theme }) => theme.color.primary};;
  `}
  
  &:hover{
    .trash_wrapper{
      display: block;
    }
  }
`;

const DropzoneBackground = styled.div`
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background: rgba(106, 103, 212, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  svg {
    font-size: 55px;
    margin-bottom: 20px;
  }

  * {
    color: white;
  }
`

const DropzoneWrapper = styled.div``

const ImagesContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.XXS};
`

const ImagesWrapper = styled.div`
  width: 100%;
  min-height: 140px;
  background: ${({ theme }) => theme.color.background};
  padding-bottom: ${({ theme }) => theme.variable.borderRadius};
  overflow: hidden;
  padding: ${({ theme }) => theme.space.S};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  
  > div {
    width: 100%;
    height: 100%;
  }
`
